'use client';

import {API} from 'common/api';
import {FRONTEND_URL} from 'config';
import {useAuth} from 'iq-auth';
import {UserGroup} from 'iq-auth/types';
import {usePathname} from 'next/navigation';
import {useEffect, useState} from 'react';

export function useSubscribeHref() {
  const {user} = useAuth();
  const pathname = usePathname();
  const next = encodeURIComponent(pathname);

  if (!user.loggedIn) return `${FRONTEND_URL}/signup?next=${next}`;
  if (user.user_group_id === UserGroup.Free) return `${FRONTEND_URL}/pricing?next=${next}`;
  return `${FRONTEND_URL}/pricing`;
}

type PricingTableResponse = {
  products: {
    name: string;
    prices: {name: string; variant: string; amount: number}[];
  }[];
};

export type PremiumPrices = {yearly: number; monthly: number};

/**
 * Live IQ Premium list prices in dollars: the `control` Stripe prices that /pricing shows
 * by default. Read from the pricing table rather than hardcoded so paywall copy cannot
 * drift from what checkout charges. `null` until loaded, or if the request fails.
 */
export function usePremiumPrices(): PremiumPrices | null {
  const [prices, setPrices] = useState<PremiumPrices | null>(null);

  useEffect(() => {
    let cancelled = false;
    API.get('/v1/stripe/pricing-tables/subscription')
      .then((table: PricingTableResponse) => {
        const premium = table.products.find((product) => product.name === 'IQ Premium');
        const amount = (name: string) =>
          premium?.prices.find((price) => price.name === name && price.variant === 'control')
            ?.amount;
        const yearly = amount('yearly');
        const monthly = amount('monthly');
        if (!cancelled && yearly !== undefined && monthly !== undefined) {
          setPrices({yearly, monthly});
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return prices;
}
